import * as React from 'react'

import dynamic from 'next/dynamic'
import withHydrationOnDemand from 'react-hydration-on-demand'

import { useAmp } from 'next/amp'

import {
  i18n,
  useConfig,
  useSiteDefinition,
  useSiteConfig,
  useRequestConfig,
  Routes,
  NextLink,
  useTheme,
} from '@thg-commerce/enterprise-core'
import { PageType } from '@thg-commerce/enterprise-core/src/App/types'
import { Footer as FooterComponent } from '@thg-commerce/gravity-patterns'
import { CopyrightLogo } from '@thg-commerce/gravity-patterns/Footer/theme'

import { HeaderGroup } from '../../HeaderGroup'
import { WidgetBannerProps } from './WidgetBanner'

const WidgetBannerComponent = dynamic<WidgetBannerProps>(
  () => import('./WidgetBanner').then((mod) => mod.WidgetBanner),
  { ssr: true },
)

const FooterGlobalAccredditationsIconsComponent = dynamic<{}>(
  () =>
    import('@thg-commerce/enterprise-widget-global-accreditations').then(
      (mod) => mod.FooterGlobalAccredditationsIcons,
    ),
  { ssr: true },
)

const FooterGlobalAccredditationsIcons = withHydrationOnDemand({
  on: ['visible'],
})(FooterGlobalAccredditationsIconsComponent)

const WidgetBanner = withHydrationOnDemand({
  on: [['delay', 500], 'idle'],
})(WidgetBannerComponent)

const Footer = withHydrationOnDemand({
  on: ['visible'],
})(FooterComponent)

export interface FullHeaderLayoutProps {
  features?: {
    stickyHeader: boolean
    showStripBanner: boolean
    wishlistNotification: boolean
  }
  beforeMainContent?: React.ReactNode
  children: React.ReactNode
  pageLayoutData?: any
  pageType?: PageType
  $window?: Window & { Optanon?: { ToggleInfoDisplay?: () => void } }
}

export const FullHeaderLayout = ({
  features = {
    stickyHeader: true,
    showStripBanner: true,
    wishlistNotification: true,
  },
  beforeMainContent,
  children,
  ...props
}: FullHeaderLayoutProps) => {
  const year = new Date().getFullYear().toString()
  const isAmp = useAmp()
  const { brand, siteName, domain } = useSiteDefinition()
  const { userAgent } = useRequestConfig()
  const { assetPrefix } = useConfig()
  const theme = useTheme()
  const {
    countryDomainMap,
    showFooterSubsiteSelector,
    showOptanonFooterLink,
    hideCopyrightLogo,
    useIngenuityLogo,
    hasFooterCopyrightAboveLogo,
    hasFooterCopyrightIcons,
    useDarkSchemeForEastendFooter,
    useWidgetForAccreditationIcons,
  } = useSiteConfig()

  const navigationData = props.pageLayoutData
  const topLevelNavData =
    navigationData &&
    navigationData.footer &&
    navigationData?.footer.navigation.topLevel

  const paymentMethodsI18nText = {
    afterPay: i18n('paymentmethods.afterpay.svg.title'),
    alipay: i18n('paymentmethods.alipay.svg.title'),
    alipayPlus: i18n('paymentmethods.alipay.plus.svg.title'),
    atome: i18n('paymentmethods.atome.svg.title'),
    americanExpress: i18n('paymentmethods.american.express.svg.title'),
    applePay: i18n('paymentmethods.apple.pay.svg.title'),
    arvato: i18n('paymentmethods.arvato.svg.title'),
    bancontact: i18n('paymentmethods.bancontact.svg.title'),
    clearpay: i18n('paymentmethods.clearpay.svg.title'),
    dinersClub: i18n('paymentmethods.diners.club.svg.title'),
    discover: i18n('paymentmethods.discover.svg.title'),
    elo: i18n('paymentmethods.elo.svg.title'),
    giftCard: i18n('paymentmethods.gift.card.svg.title'),
    googlePay: i18n('paymentmethods.google.pay.svg.title'),
    hiper: i18n('paymentmethods.hiper.svg.title'),
    ideal: i18n('paymentmethods.ideal.svg.title'),
    interac: i18n('paymentmethods.interac.svg.title'),
    jcb: i18n('paymentmethods.jcb.svg.title'),
    kcp: i18n('paymentmethods.kcp.svg.title'),
    klarna: i18n('paymentmethods.klarna.svg.title'),
    laybuy: i18n('paymentmethods.laybuy.svg.title'),
    maestro: i18n('paymentmethods.maestro.svg.title'),
    mastercard: i18n('paymentmethods.mastercard.svg.title'),
    molpay: i18n('paymentmethods.molpay.svg.title'),
    multibanco: i18n('paymentmethods.multibanco.svg.title'),
    one4All: i18n('paymentmethods.one4all.svg.title'),
    openpay: i18n('paymentmethods.openpay.svg.title'),
    paypal: i18n('paymentmethods.paypal.svg.title'),
    paytm: i18n('paymentmethods.paytm.svg.title'),
    primeiropay: i18n('paymentmethods.primeiropay.svg.title'),
    rupay: i18n('paymentmethods.rupay.svg.title'),
    sepa: i18n('paymentmethods.sepa.svg.title'),
    solo: i18n('paymentmethods.solo.svg.title'),
    splitIt: i18n('paymentmethods.splitit.svg.title'),
    spotii: i18n('paymentmethods.spotii.svg.title'),
    tenpay: i18n('paymentmethods.tenpay.svg.title'),
    trustPay: i18n('paymentmethods.trust.pay.svg.title'),
    unionpay: i18n('paymentmethods.unionpay.svg.title'),
    visaDebit: i18n('paymentmethods.visa.debit.svg.title'),
    visaCredit: i18n('paymentmethods.visa.credit.svg.title'),
    visaElectron: i18n('paymentmethods.visa.electron.svg.title'),
    webMoney: i18n('paymentmethods.web.money.svg.title'),
    wechatPay: i18n('paymentmethods.wechat.pay.svg.title'),
    yandex: i18n('paymentmethods.yandex.svg.title'),
    zippay: i18n('paymentmethods.zippay.svg.title'),
  }

  const copyrightText = i18n('thgcopyright.copyrighttext')

  const footerI18nText = {
    customerEngagement: {
      connectWithUs: {
        text: i18n('footer.social.icons.title.text'),
      },
      newsletterSignUp: {
        text: i18n('account.emailsignup.instruction'),
        buttonText: i18n('footer.newsletter.signup.submit.button.text'),
      },
    },
    copyrightAndPayments: {
      paymentMethods: {
        text: i18n('footer.links.title.pay.securely.with'),
      },
      copyright: {
        text: copyrightText && `${year} ${copyrightText}`,
        ariaMomentumText: 'thgcopyright.momentumtext',
      },
    },
    navigation: {
      countrySelector: {
        selectorLabel: i18n('footer.countryselector.label'),
      },
    },
  }

  const footerRouteLinks = {
    customerEngagement: {
      newsletterSignUp: {
        signUpLink: Routes.NewsletterSignUp,
      },
    },
  }

  const flagPath =
    countryDomainMap &&
    `${assetPrefix}/static/flags/1x1/${countryDomainMap[domain]}.svg`

  const iconDisplayed = () => {
    if (theme.patterns.footer.copyrightAndPayments.copyrightLogo) {
      return theme.patterns.footer.copyrightAndPayments.copyrightLogo
    }
    if (useIngenuityLogo) {
      return CopyrightLogo.ingenuity
    }
    return CopyrightLogo.thg
  }

  const isDark = useDarkSchemeForEastendFooter || false

  return (
    <React.Fragment>
      <HeaderGroup
        wishlistNotification={features.wishlistNotification}
        stickyHeader={features.stickyHeader}
        flagPath={flagPath}
        userAgent={userAgent}
        headerData={navigationData}
        brandsData={navigationData}
      >
        <WidgetBanner
          userAgent={userAgent}
          showStripBanner={features.showStripBanner}
          widgetData={navigationData}
          pageType={props.pageType}
        />
      </HeaderGroup>
      {beforeMainContent && beforeMainContent}
      <main id="main-content">{children}</main>
      <Footer
        useWidgetForAccreditationIcons={useWidgetForAccreditationIcons || false}
        userAgent={userAgent}
        isAmp={isAmp}
        isDark={isDark}
        customerEngagement={{
          isDark,
          connectWithUs: {
            isDark,
            text: footerI18nText.customerEngagement.connectWithUs.text,
            socialAccounts: navigationData?.socialAccounts,
            actionComponent: NextLink,
          },
          newsletterSignUp: {
            isDark,
            text: footerI18nText.customerEngagement.newsletterSignUp.text,
            buttonText:
              footerI18nText.customerEngagement.newsletterSignUp.buttonText,
            signUpLink:
              footerRouteLinks.customerEngagement.newsletterSignUp.signUpLink,
          },
        }}
        navigation={{
          isDark,
          isAmp,
          userAgent,
          currentBrand: brand,
          topLevel: topLevelNavData,
          showSubsiteSelector: showFooterSubsiteSelector,
          showCookieSettings: showOptanonFooterLink,
          cookieSettingsHandler: () => {
            props.$window?.Optanon?.ToggleInfoDisplay &&
              props.$window?.Optanon?.ToggleInfoDisplay()
          },
          countrySelector: {
            flagPath,
            domain,
            countryDomainMap,
            i18nLabel: footerI18nText.navigation.countrySelector.selectorLabel,
            brand: getBrandFromSitename(siteName),
          },
          actionComponent: NextLink,
          cookieConsentButtonText: i18n('stickyoptanoncookiebuttonlink'),
        }}
        copyrightAndPayments={{
          paymentMethods: {
            paymentMethodsI18nText,
            text: footerI18nText.copyrightAndPayments.paymentMethods.text,
            paymentProviders: navigationData?.paymentMethods,
          },
          copyright: {
            hasFooterCopyrightAboveLogo,
            hasFooterCopyrightIcons,
            text: footerI18nText.copyrightAndPayments.copyright.text,
            ariaMomentumText:
              footerI18nText.copyrightAndPayments.copyright.ariaMomentumText,
            displayIcon: !hideCopyrightLogo ? iconDisplayed() : undefined,
          },
        }}
        Accreditations={FooterGlobalAccredditationsIcons}
      />
    </React.Fragment>
  )
}

export const getBrandFromSitename = (siteName: string) => {
  return siteName?.split(' ')[0]
}
